import { Card, CardContent, Link, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Icons, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  PersonalInformationType,
  UserAddressFragment,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

type ShippingAddressProps = {
  loading?: boolean
  fullName?: string
  address?: Maybe<UserAddressFragment['address']>
  isPhoneNumberVerified?: boolean
  isDeletedUser?: boolean
  phoneNumber?: string
  rightLink?: string
  hideVerified?: boolean
  personalInformationRequirements: string[]
}

export const ShippingAddress: Organism<ShippingAddressProps> = ({
  loading,
  fullName,
  address,
  isPhoneNumberVerified,
  isDeletedUser,
  phoneNumber,
  rightLink,
  hideVerified,
  personalInformationRequirements,
}) => {
  const { t } = useTranslation('storePurchaseOrderDetail')
  const hasError = useMemo<boolean>(
    () =>
      // 氏名未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.FullName
      ) &&
        !fullName) ||
      // 電番未認証
      (personalInformationRequirements.includes(
        PersonalInformationType.PhoneNumber
      ) &&
        !isPhoneNumberVerified &&
        !hideVerified) ||
      // 住所未登録
      (personalInformationRequirements.includes(
        PersonalInformationType.Address
      ) &&
        !address?.fullAddress),
    [fullName, personalInformationRequirements, address, isPhoneNumberVerified]
  )

  if (loading) {
    return <ShippingAddressSkeleton />
  }

  return (
    <CellFrame sx={{ pt: 3, pb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" color="text.secondary">
          {t('personalInfo')}
        </Typography>
        {!!rightLink && !isDeletedUser && (
          <Link variant="h5" href={rightLink}>
            {t('viewDetail', '詳細を見る')}
          </Link>
        )}
      </Stack>
      <Card elevation={0} sx={{ borderRadius: 2, mt: 3 }}>
        <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
          {isDeletedUser ? (
            <Typography variant="h6">{t('deletedUser')}</Typography>
          ) : (
            <Stack>
              {hasError && (
                <Typography variant="body2" color="error.main">
                  {t(
                    'personalInfoError',
                    '商品の購入にはお客様情報の登録が必要です。'
                  )}
                </Typography>
              )}
              <Stack spacing={1} sx={{ pt: hasError ? 2 : 0 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('familyName', '氏名')}
                </Typography>
                <Typography
                  variant="body2"
                  color={fullName ? 'text.primary' : 'text.hint'}
                >
                  {fullName ??
                    t('unregisteredFullName', '氏名が登録されていません')}
                </Typography>
              </Stack>
              {personalInformationRequirements.includes(
                PersonalInformationType.Address
              ) && (
                <Stack spacing={1} sx={{ pt: 2 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('address')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={address?.fullAddress ? 'text.primary' : 'text.hint'}
                  >
                    {address?.fullAddress ?? t('unregisteredAddress')}
                  </Typography>
                </Stack>
              )}
              <Stack spacing={1} sx={{ pt: 2 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('phoneNumber')}
                  </Typography>
                  {!isPhoneNumberVerified && !hideVerified ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icons.Error
                        fontSize="small"
                        sx={{ color: 'error.main', mt: -1 }}
                      />
                      <Typography variant="body2" color="error.main">
                        {t('unverify')}
                      </Typography>
                    </Stack>
                  ) : (
                    <>
                      {!hideVerified && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icons.Success
                            fontSize="small"
                            sx={{ color: 'text.hint', mt: -1 }}
                          />
                          <Typography variant="body2" color="text.hint">
                            {t('verified')}
                          </Typography>
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
                <Typography variant="body2">{phoneNumber}</Typography>
                {!phoneNumber && (
                  <Typography variant="body2" color="text.hint">
                    {t('unregistered')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </CellFrame>
  )
}

const ShippingAddressSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5, mt: 5 }}>
    <SkeletonText variant="h3" width="50%" />
    <Stack sx={{ pt: 1 }}>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
          {Array.from(Array(3).keys()).map(key => (
            <Stack flex={1} key={key}>
              <SkeletonText variant="small" width="50%" />
              <SkeletonText variant="small" width="100%" />
            </Stack>
          ))}
        </CardContent>
      </Card>
    </Stack>
  </CellFrame>
)
